import React, { useEffect } from 'react';
import SEO from '../components/common/SEO';
import Layout from '../components/layout/Layout';
import ShopMegaHero from '../components/home/ShopMegaHero';
import TrustBar from '../components/home/TrustBar';
import WhatIsFlashUSDT from '../components/home/WhatIsFlashUSDT';
import Features from '../components/home/Features';
import HowItWorks from '../components/home/HowItWorks';
import NetworkComparison from '../components/home/NetworkComparison';
import WalletCompatibility from '../components/home/WalletCompatibility';
import Audience from '../components/home/Audience';
import UseCases from '../components/home/UseCases';
import Pricing from '../components/home/Pricing';
import SecurityTrust from '../components/home/SecurityTrust';
import Testimonials from '../components/home/Testimonials';
import LatestPosts from '../components/home/LatestPosts';
import LongFAQ, { FAQS } from '../components/home/LongFAQ';
import CTA from '../components/home/CTA';
import { siteConfig } from '../config/site';

const HomePage: React.FC = () => {
  useEffect(() => {
    const store = {
      '@context': 'https://schema.org',
      '@type': 'Store',
      name: siteConfig.brand.name,
      url: siteConfig.domain,
      logo: `${siteConfig.domain}/logo512.png`,
      description: siteConfig.brand.description,
      email: siteConfig.contact.orderEmail,
      contactPoint: [{
        '@type': 'ContactPoint',
        contactType: 'customer support',
        telephone: siteConfig.contact.phoneDisplay,
        availableLanguage: ['English'],
      }],
    };
    const website = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteConfig.brand.name,
      url: siteConfig.domain,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteConfig.domain}/blog?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const product = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${siteConfig.brand.name} — Flash USDT Software License`,
      description: siteConfig.brand.description,
      brand: { '@type': 'Brand', name: siteConfig.brand.name },
      category: 'Software · Cryptocurrency Tools',
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '15',
        highPrice: '5000',
        offerCount: '6',
        url: `${siteConfig.domain}/pricing`,
      },
    };
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteConfig.domain },
        { '@type': 'ListItem', position: 2, name: 'Pricing', item: `${siteConfig.domain}/pricing` },
        { '@type': 'ListItem', position: 3, name: 'Blog', item: `${siteConfig.domain}/blog` },
      ],
    };
    const tags: HTMLScriptElement[] = [];
    for (const [id, obj] of [
      ['ld-store', store],
      ['ld-website', website],
      ['ld-faq', faq],
      ['ld-product', product],
      ['ld-breadcrumb', breadcrumb],
    ] as const) {
      let s = document.getElementById(id) as HTMLScriptElement | null;
      if (!s) {
        s = document.createElement('script');
        s.id = id; s.type = 'application/ld+json';
        document.head.appendChild(s);
      }
      s.textContent = JSON.stringify(obj);
      tags.push(s);
    }
    return () => tags.forEach((t) => t.remove());
  }, []);

  return (
    <Layout>
      <SEO
        title={`${siteConfig.brand.name} — Buy Flash USDT Software Online (TRC-20, ERC-20, BEP-20)`}
        description={`Buy licensed flash USDT software from ${siteConfig.brand.name}. Instant email delivery, multi-chain TRC-20/ERC-20/BEP-20, 50+ compatible wallets, 24/7 WhatsApp support, 365-day flash validity — plans from $15.`}
        keywords="flash USDT software shop, buy flash USDT software, flash USDT software online, flash USDT license, TRC-20 flash USDT, ERC-20 flash USDT, BEP-20 flash USDT, USDT flashing tool, tether flasher, USDT simulator, buy USDT flasher"
      />
      <ShopMegaHero />
      <TrustBar />
      <WhatIsFlashUSDT />
      <Features />
      <HowItWorks />
      <NetworkComparison />
      <WalletCompatibility />
      <Audience />
      <UseCases />
      <Pricing />
      <SecurityTrust />
      <Testimonials />
      <LatestPosts />
      <LongFAQ />
      <CTA />
    </Layout>
  );
};

export default HomePage;
